// The `?net` debug overlay stage 3's own brief asks for: everything needed to judge "does this feel
// lag-free" with numbers instead of by eye, since neither a human nor this tooling can reliably feel the
// difference between 40ms and 120ms of latency. Purely a readout — nothing here feeds back into gameplay.
export type NetStats = {
  rttMs: number; jitterMs: number; leadTicks: number; unacked: number;
  /** `null` until at least one prediction has actually been compared against what the server reported —
      shown as "n/a", never as a numeric 0 a viewer could mistake for a measured perfect prediction. */
  predErrMeanM: number | null; predErrMaxM: number | null;
  correctionsPerSec: number; snapshotAgeMs: number;
  interpDelayMs: number;
  /** Snapshot arrivals net/entityInterp.ts discarded as a likely local stall (a backgrounded/throttled tab,
      a long GC pause) rather than real network jitter — shown only when non-zero, so a contaminated-looking
      `interp delay` is visibly explained instead of silently smoothed into a number that looks plausible in
      isolation (a live review caught exactly this: 85ms of interp delay on a 2ms-RTT connection, with
      nothing on the overlay saying why). */
  interpStalls: number;
};

export function createNetOverlay(container: HTMLElement) {
  const el = document.createElement('pre');
  el.id = 'net-overlay';
  el.style.cssText = 'position:absolute;left:10px;top:44px;margin:0;padding:6px 8px;font:11px/1.5 monospace;'
    + 'background:rgba(239,227,198,.92);color:#2c1e12;border:1px solid #3b2a1c;pointer-events:none;z-index:6;white-space:pre';
  container.appendChild(el);

  function update(s: NetStats) {
    const fmt = (v: number | null) => v === null ? 'n/a' : `${v.toFixed(3)}m`;
    el.textContent =
      `rtt ${s.rttMs.toFixed(0)}ms  jitter ${s.jitterMs.toFixed(0)}ms  lead ${s.leadTicks.toFixed(1)}t\n`
      + `unacked ${s.unacked}  snapshot age ${s.snapshotAgeMs.toFixed(0)}ms\n`
      + `pred err  mean ${fmt(s.predErrMeanM)}  worst ${fmt(s.predErrMaxM)}\n`
      + `corrections ${s.correctionsPerSec.toFixed(1)}/s  interp delay ${s.interpDelayMs.toFixed(0)}ms`
      + (s.interpStalls > 0 ? `  (${s.interpStalls} stall${s.interpStalls === 1 ? '' : 's'} discarded)` : '');
  }
  function destroy() { el.remove(); }
  return { update, destroy };
}
